import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAllPropPresets,
  selectSelectedPropPresetId,
  updateSelectedPropPreset,
} from "../../app/slices/TrainingSlice";
import ActorPresetEditorField from "../actor_presets/ActorPresetEditorField";
import PiInputfield from "../../components/PiInputField";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import usePropHelper from "../../hooks/PropHelper";
import { Prop, PropType } from "../../models/Prop";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import { useTranslation } from "react-i18next";
import {
  getPropRenderThumbnailCacheKey,
  selectCachedPropRenderThumbnails,
  selectPropCategories,
  setCachedPropRenderThumbnail,
} from "../../app/slices/PropLibrarySlice";
import PropLibraryCategoryLabel from "../prop_library/PropLibraryCategoryLabel";
import { selectCurrentOrganization } from "../../app/slices/UserSlice";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import { PropService } from "../../services/PropService";
import "./PropPresetEditor.css";

const getRenderRoutePropType = (propType: PropType): 0 | 1 | undefined => {
  if (propType === PropType.Default) return 0;
  if (propType === PropType.Situation) return 1;
  return undefined;
};

const getInspectDisplayName = (name: string) =>
  name
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

export default function PropPresetEditor() {
  const selectedPropPreset = useSelector(selectSelectedPropPresetId);
  const propPresets = useSelector(selectAllPropPresets);
  const selectedProp = propPresets.find(
    (prop) => prop.id === selectedPropPreset
  );
  const { getPropDTO } = usePropHelper();
  const propDTO = selectedProp ? getPropDTO(selectedProp?.propId) : undefined;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const noPrefabNameLabel = String(t("no_prefab_name") ?? "");
  const categories = useSelector(selectPropCategories);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const token = useSelector(selectToken);
  const cachedPropRenderThumbnails = useSelector(
    selectCachedPropRenderThumbnails
  );
  const cachedPropRenderThumbnailsRef = useRef(cachedPropRenderThumbnails);
  const inFlightThumbnailKeys = useRef(new Set<string>());
  const renderRoutePropType = selectedProp
    ? getRenderRoutePropType(selectedProp.propType)
    : undefined;
  const inspectDefinitions = propDTO?.metadata.inspectDefinitions ?? [];
  const selectedPropId = selectedProp?.propId;
  const selectedPropInstanceId = selectedProp?.id;

  useEffect(() => {
    cachedPropRenderThumbnailsRef.current = cachedPropRenderThumbnails;
  }, [cachedPropRenderThumbnails]);

  const inspectThumbnailItems = useMemo(() => {
    const seen = new Set<string>();

    return inspectDefinitions.flatMap((inspectDefinition) => {
      const displayName = getInspectDisplayName(inspectDefinition.name);
      const identity = displayName
        ? `name:${displayName.toLowerCase()}`
        : `id:${inspectDefinition.id ?? ""}`;

      if (seen.has(identity)) return [];
      seen.add(identity);

      return [
        {
          description: inspectDefinition.description,
          displayName,
          key: identity,
          renderId: inspectDefinition.id,
        },
      ];
    });
  }, [inspectDefinitions]);

  const inspectThumbnailSignature = useMemo(
    () =>
      inspectThumbnailItems
        .map((item) => `${item.renderId ?? ""}:${item.displayName}`)
        .join("|"),
    [inspectThumbnailItems]
  );

  const myCategoriesMapped = propDTO?.propContent.categories.map((category) => {
    var result = categories.find((cat) => cat.id === category.id);
    if (!result) return;
    return <PropLibraryCategoryLabel category={result} key={result.id} />;
  });

  useEffect(() => {
    if (!currentOrganization?.id || !token || !selectedPropId) return;
    if (renderRoutePropType === undefined) return;

    let cancelled = false;
    const loadMissingThumbnails = async () => {
      for (const inspectThumbnailItem of inspectThumbnailItems) {
        if (!inspectThumbnailItem.renderId) continue;

        const cacheKey = getPropRenderThumbnailCacheKey(
          renderRoutePropType,
          selectedPropId,
          inspectThumbnailItem.renderId
        );
        if (cachedPropRenderThumbnailsRef.current[cacheKey]) continue;
        if (inFlightThumbnailKeys.current.has(cacheKey)) continue;

        inFlightThumbnailKeys.current.add(cacheKey);
        const thumbnail = await PropService.GetPropRenderThumbnail(
          currentOrganization.id,
          token,
          renderRoutePropType,
          selectedPropId,
          inspectThumbnailItem.renderId
        );
        inFlightThumbnailKeys.current.delete(cacheKey);

        if (cancelled || !thumbnail?.downloadUri) continue;
        cachedPropRenderThumbnailsRef.current = {
          ...cachedPropRenderThumbnailsRef.current,
          [cacheKey]: thumbnail.downloadUri,
        };
        dispatch(
          setCachedPropRenderThumbnail({
            propType: renderRoutePropType,
            propId: selectedPropId,
            renderId: inspectThumbnailItem.renderId,
            downloadUri: thumbnail.downloadUri,
          })
        );
      }
    };

    loadMissingThumbnails();

    return () => {
      cancelled = true;
    };
  }, [
    currentOrganization?.id,
    dispatch,
    inspectThumbnailItems,
    inspectThumbnailSignature,
    renderRoutePropType,
    selectedPropId,
    token,
  ]);

  const updateSelectedProp = (field: keyof Prop, value: any) => {
    if (!selectedProp) return;
    const cpy: Prop = { ...selectedProp, [field]: value };
    dispatch(updateSelectedPropPreset(cpy));
  };

  const image = (
    <img
      className="proppreseteditor-previewimage"
      src={propDTO?.propContent.downloadUri}
      alt={"Prop Thumbnail"}
    />
  );
  const noImage = (
    <div className="proppreseteditor-previewempty">
      <ViewInArOutlinedIcon fontSize="inherit" color="inherit" />
    </div>
  );
  const propPreview = propDTO?.propContent.downloadUri ? image : noImage;

  const inspectThumbnails =
    inspectThumbnailItems.length > 0 ? (
      <div className="proppreseteditor-inspectgrid">
        {inspectThumbnailItems.map((inspectThumbnailItem) => {
          const cacheKey =
            inspectThumbnailItem.renderId &&
            renderRoutePropType !== undefined &&
            selectedPropId
              ? getPropRenderThumbnailCacheKey(
                  renderRoutePropType,
                  selectedPropId,
                  inspectThumbnailItem.renderId
                )
              : undefined;
          const thumbnailUri = cacheKey
            ? cachedPropRenderThumbnails[cacheKey]
            : undefined;

          return (
            <div
              className="proppreseteditor-inspectitem"
              key={`${selectedPropInstanceId ?? selectedPropId}:${inspectThumbnailItem.key}`}
            >
              <div className="proppreseteditor-inspectimagewrap">
                {thumbnailUri ? (
                  <img
                    className="proppreseteditor-inspectimage"
                    src={thumbnailUri}
                    alt={inspectThumbnailItem.displayName}
                  />
                ) : (
                  <div className="proppreseteditor-inspectempty">
                    <PhotoLibraryOutlinedIcon
                      fontSize="inherit"
                      color="inherit"
                    />
                  </div>
                )}
              </div>
              <div className="proppreseteditor-inspectname">
                {inspectThumbnailItem.displayName}
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div className="proppreseteditor-inspectnone">No inspect definitions</div>
    );

  return (
    <div className="proppreseteditor-shell">
      <header
        className="proppreseteditor-pageheader"
        data-testid="properties-header"
      >
        <div className="proppreseteditor-pageheader-icon">
          <Inventory2OutlinedIcon fontSize="inherit" />
        </div>
        <div className="proppreseteditor-pageheader-copy">
          <div className="proppreseteditor-pageheader-eyebrow">Prop preset</div>
          <h1>Editing Prop Preset</h1>
        </div>
      </header>
      <main
        aria-label="actor-preset-window-root"
        className="proppreseteditor-scroll pit-creator-scrollbar"
      >
        {selectedProp && (
          <div className="proppreseteditor-grid">
            <div
              className="proppreseteditor-previewcolumn"
              aria-label={"prop-preset-editor-col-1 h-full"}
            >
              <ActorPresetEditorField
                name={t("prop_preview")}
                icon={ViewInArOutlinedIcon}
                className="proppreseteditor-previewtile"
                contentClassName="proppreseteditor-previewcontent"
              >
                {propPreview}
              </ActorPresetEditorField>
            </div>
            <div
              aria-label={"prop-preset-editor-col-2"}
              className="proppreseteditor-detailscolumn"
            >
              <ActorPresetEditorField
                name={t("prop_name")}
                icon={ViewInArOutlinedIcon}
                className="proppreseteditor-detailtile"
              >
                <PiInputfield
                  label=""
                  onChange={(newValue) => updateSelectedProp("name", newValue)}
                  placeholder="Working name..."
                  value={selectedProp?.name}
                />
              </ActorPresetEditorField>
              <ActorPresetEditorField
                name={t("prefab_name")}
                icon={Inventory2OutlinedIcon}
                className="proppreseteditor-detailtile"
              >
                <div
                  className={`proppreseteditor-readonlyvalue${
                    selectedProp.prefabName
                      ? ""
                      : " proppreseteditor-readonlyvalue-empty"
                  }`}
                  title={selectedProp.prefabName || noPrefabNameLabel}
                >
                  {selectedProp.prefabName || noPrefabNameLabel}
                </div>
              </ActorPresetEditorField>
              <ActorPresetEditorField
                name={t("categories")}
                icon={CategoryOutlinedIcon}
                className="proppreseteditor-detailtile"
              >
                <div className="proppreseteditor-chiplist">
                  {myCategoriesMapped}
                </div>
              </ActorPresetEditorField>
              <ActorPresetEditorField
                name={t("inspect_thumbnails")}
                icon={PhotoLibraryOutlinedIcon}
                className="proppreseteditor-detailtile"
              >
                {inspectThumbnails}
              </ActorPresetEditorField>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
